import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  FormControl,
  Grid,
  type GridProps,
  InputLabel,
  TextField,
} from "@mui/material";
import type { FormikProps } from "formik";
import { debounce } from "lodash";

import {
  getUnNaNumbersAPI,
  type UnNaNumberOption,
} from "../../../API/getUnNaNumbers";
import { FORM_STYLES } from "../../../constants";

export interface FormikUnNaNumberAutocompleteProps {
  formik: FormikProps<any>;
  name: string;
  unNumberSequence?: string; // Optional name of another form field to set with the selected option's sequence number
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  gridProps?: GridProps;
  minSearchLength?: number;
  debounceMs?: number;
  page?: number;
  limit?: number;
  /**
   * Optional mapping to set additional form fields when a UN/NA is selected.
   * Example: { hazmatClass: (opt) => opt.class, packingGroup: (opt) => opt.packingGroup }
   */
  setFieldsFromOption?: Record<string, (opt: UnNaNumberOption) => unknown>;
}

const toTrimmed = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const normalizeSearch = (value: string): string => value.trim().toUpperCase();

const formatOption = (option: UnNaNumberOption): string => {
  const hazardClass = toTrimmed((option as any)?.class);

  const parts = [
    toTrimmed(option?.id),
    toTrimmed(option?.name),
    hazardClass,
    toTrimmed(option?.packingGroup),
    toTrimmed(option?.label),
  ].filter(v => v)

  // Always show something meaningful
  return parts.join(", ") || toTrimmed(option?.id) || "";
};

const optionKey = (option: UnNaNumberOption): string => {
  const hazardClass = toTrimmed((option as any)?.class);
  return [
    toTrimmed(option?.id),
    hazardClass,
    toTrimmed(option?.packingGroup),
    toTrimmed(option?.label),
    toTrimmed(option?.name),
  ]
    .filter(Boolean)
    .join("|");
};

const dedupeOptions = (items: UnNaNumberOption[]): UnNaNumberOption[] => {
  const seen = new Set<string>();
  const out: UnNaNumberOption[] = [];
  for (const item of items) {
    const key = optionKey(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const getNoOptionsText = (
  searchText: string,
  loading: boolean,
  min: number,
) => {
  if ((searchText?.length ?? 0) < min) return `Type at least ${min} characters`;
  return "Records not found";
};

const FormikUnNaNumberAutocomplete: React.FC<
  FormikUnNaNumberAutocompleteProps
> = ({
  formik,
  name,
  unNumberSequence,
  label = "UN/NA#",
  placeholder = "Search UN/NA#",
  required,
  disabled,
  gridProps = { md: 3, xs: 12 },
  minSearchLength = 3,
  debounceMs = 500,
  page = 1,
  limit = 10,
  setFieldsFromOption,
}) => {
  // Both unNumber and unNumberSequence from formik
  const unNumberValue = formik.values?.[name] ?? "";
  const unNumberSequenceValue =
    formik.values?.[unNumberSequence ?? ""] ?? undefined;

  const [options, setOptions] = useState<UnNaNumberOption[]>([]);
  const [loading, setLoading] = useState(false);

  // What user sees/types into the textbox (show full label for selected)
  const [inputValue, setInputValue] = useState<string>("");
  // Only what triggers search (updated only on actual user typing)
  const [searchText, setSearchText] = useState<string>("");

  // When we clear a pre-populated selection because the user starts typing,
  // we don't want the formik->inputValue sync effect to wipe the user's typed text.
  const suppressNextFormikSyncRef = useRef(false);

  // Find selected option by both id and sequence
  const selected: UnNaNumberOption | undefined = useMemo(() => {
    if (!unNumberValue) return undefined;
    let found = options.find(
      (opt) =>
        opt?.id === unNumberValue &&
        (unNumberSequence
          ? opt?.sequence?.toString() === unNumberSequenceValue?.toString()
          : true),
    );
    if (found) return found;

    // If not found in options, return a stub for display
    return {
      id: unNumberValue?.toString(),
      sequence: unNumberSequenceValue,
      name: "",
      class: "",
      label: "",
      packingGroup: "",
    };
  }, [unNumberValue, unNumberSequence, unNumberSequenceValue, options]);

  // Fetch options for search
  const fetchOptions = useCallback(
    async (search: string) => {
      const normalized = normalizeSearch(search);
      if ((normalized?.length ?? 0) < minSearchLength) return;

      setLoading(true);
      try {
        const response = await getUnNaNumbersAPI({
          search: normalized,
          page,
          limit,
        });
        const next = Array.isArray(response?.data) ? response.data : [];
        setOptions(dedupeOptions(next));
      } catch (_e) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [limit, minSearchLength, page],
  );

  // On mount or when unNumberValue changes (edit mode), fetch option for current value
  // Prevent infinite rerender loop by only setting Formik values if changed
  const didSetFormikRef = useRef(false);
  // Helper to clear all relevant state and formik fields
  const clearAllFields = useCallback(() => {
    setInputValue("");
    setOptions([]);
    setSearchText("");
    if (formik.values[name] !== "") formik.setFieldValue(name, "");
    if (unNumberSequence && formik.values[unNumberSequence] !== "")
      formik.setFieldValue(unNumberSequence, "");
  }, [formik, name, unNumberSequence]);

  const applySingleOption = useCallback(
    (only: UnNaNumberOption) => {
      if (formik.values[name] !== only.id) {
        didSetFormikRef.current = true;
        formik.setFieldValue(name, only.id);
      }
      if (
        unNumberSequence &&
        formik.values[unNumberSequence] !== only.sequence
      ) {
        formik.setFieldValue(unNumberSequence, only.sequence);
      }
      setInputValue(formatOption(only));
    },
    [formik, name, unNumberSequence],
  );

  const handleFetchedOptions = useCallback(
    (deduped: UnNaNumberOption[]) => {
      setOptions(deduped);
      setSearchText(unNumberValue);

      if (deduped.length === 1) {
        applySingleOption(deduped[0]);
        return;
      }

      const match = deduped.find(
        (opt) =>
          opt?.id === unNumberValue &&
          (unNumberSequence
            ? opt?.sequence?.toString() === unNumberSequenceValue?.toString()
            : true),
      );

      if (match) {
        setInputValue(formatOption(match));
      } else {
        setInputValue("");
        if (formik.values[name] !== "") formik.setFieldValue(name, "");
        if (unNumberSequence && formik.values[unNumberSequence] !== "")
          formik.setFieldValue(unNumberSequence, "");
      }
    },
    [
      formik,
      name,
      unNumberSequence,
      unNumberValue,
      applySingleOption,
      unNumberSequenceValue,
    ],
  );

  useEffect(() => {
    if (!unNumberValue) {
      clearAllFields();
      return;
    }
    let cancelled = false;
    const fetchAndHandle = async () => {
      try {
        const response = await getUnNaNumbersAPI({
          search: unNumberValue,
          page: 1,
          limit: 20,
        });
        const deduped = dedupeOptions(
          Array.isArray(response?.data) ? response.data : [],
        );
        if (!cancelled) handleFetchedOptions(deduped);
      } catch {
        if (!cancelled) clearAllFields();
      }
    };
    fetchAndHandle();
    return () => {
      cancelled = true;
    };
  }, [
    unNumberValue,
    unNumberSequence,
    unNumberSequenceValue,
    formik,
    name,
    clearAllFields,
    handleFetchedOptions,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(debounce(fetchOptions, debounceMs), [
    fetchOptions,
    debounceMs,
  ]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  // When user types, update inputValue and searchText
  useEffect(() => {
    if ((searchText?.length ?? 0) < minSearchLength) {
      setOptions([]);
      setLoading(false);
      return;
    }
    debouncedFetch(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // When formik value changes externally and not from user typing, update inputValue for display
  useEffect(() => {
    if (suppressNextFormikSyncRef.current) {
      suppressNextFormikSyncRef.current = false;
      return;
    }
    // Prevent rerender loop: if we just set Formik value, skip this render
    if (didSetFormikRef.current) {
      didSetFormikRef.current = false;
      return;
    }
    if (selected) {
      setInputValue(formatOption(selected));
    } else if (unNumberValue) {
      setInputValue(unNumberValue);
    } else {
      setInputValue("");
    }
  }, [selected, unNumberValue]);

  const touched = Boolean((formik.touched as any)?.[name]);
  const rawError = touched ? (formik.errors as any)?.[name] : undefined;
  const error = Boolean(rawError);

  return (
    <Grid item {...gridProps}>
      <FormControl style={{ minWidth: 120 }} fullWidth size="small">
        <InputLabel style={FORM_STYLES.LABEL} shrink={true} id={`${name}Label`}>
          {label} {required && <sup>*</sup>}
        </InputLabel>

        <Autocomplete<UnNaNumberOption, false, true, false>
          className="custom-autocomplete"
          disablePortal
          forcePopupIcon={false}
          selectOnFocus={false}
          clearOnBlur={false}
          disabled={Boolean(disabled)}
          options={options}
          value={selected}
          disableClearable
          inputValue={inputValue}
          filterOptions={(allOptions) => allOptions}
          getOptionLabel={(option) => formatOption(option)}
          isOptionEqualToValue={(option, value) =>
            optionKey(option) === optionKey(value)
          }
          noOptionsText={getNoOptionsText(inputValue, loading, minSearchLength)}
          onInputChange={(_e, newInputValue, reason) => {
            setInputValue(newInputValue);
            if (reason === "input") {
              // If a pre-populated value exists and the user starts typing a new search,
              // clear the selected form value but keep what they typed.
              const currentValue = (unNumberValue ?? "").toString();
              const currentSequence = unNumberSequenceValue ?? 0;
              if (
                currentValue &&
                newInputValue !==
                  formatOption(
                    selected ?? {
                      id: currentValue,
                      name: "",
                      sequence: currentSequence,
                      class: "",
                      label: "",
                      packingGroup: "",
                    },
                  )
              ) {
                suppressNextFormikSyncRef.current = true;
                formik.setFieldValue(name, "");
                if (unNumberSequence)
                  formik.setFieldValue(unNumberSequence, "");
              }
              setSearchText(newInputValue);
            }
          }}
          onChange={(_e, newValue, reason) => {
            if (reason === "clear") {
              formik.setFieldValue(name, "");
              if (unNumberSequence) formik.setFieldValue(unNumberSequence, "");
              setInputValue("");
              setSearchText("");
              setOptions([]);
              return;
            }
            // Set both unNumber and unNumberSequence in formik
            formik.setFieldValue(name, newValue?.id ?? "");
            if (unNumberSequence)
              formik.setFieldValue(unNumberSequence, newValue?.sequence ?? "");
            setInputValue(newValue ? formatOption(newValue) : "");
            setSearchText("");

            if (newValue && setFieldsFromOption) {
              Object.entries(setFieldsFromOption).forEach(
                ([fieldName, getter]) => {
                  formik.setFieldValue(fieldName, getter(newValue));
                },
              );
            }
          }}
          onBlur={() => formik.setFieldTouched(name, true)}
          renderOption={(props, option) => (
            <li {...props} key={optionKey(option)} title={formatOption(option)}>
              {formatOption(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete="off"
              name={name}
              placeholder={placeholder}
              variant="outlined"
              error={error}
              helperText={error ? rawError?.toString() : undefined}
              inputProps={{
                ...params.inputProps,
                "data-testid": `${name}-input`,
                id: `${name}-input`,
                title: inputValue,
              }}
            />
          )}
        />
      </FormControl>
    </Grid>
  );
};

export default FormikUnNaNumberAutocomplete;
